import { API_BASE_URL } from '../config';

// Only current server-relative progress references are accepted. No query credentials,
// external URLs, encoded separators or alternate API routes can receive the bearer.
export async function requestProgressImage(service, userId, { method = 'GET', image, signal, imageUrl } = {}) {
 const api = new URL(API_BASE_URL || window.location.origin, window.location.origin);
 const path = `/api/users/${encodeURIComponent(userId)}/progress-image`;
 if (imageUrl && (typeof imageUrl !== 'string' || !imageUrl.startsWith(`${path}/`) || !/^[a-zA-Z0-9._-]+$/.test(imageUrl.slice(path.length + 1)) || imageUrl.includes('..'))) {
  throw new Error('Invalid progress image reference.');
 }
 const target = new URL(imageUrl || path, api);
 if (target.origin !== api.origin) throw new Error('Invalid progress image reference.');
 const identity = service.authenticatedIdentity;
 const controller = new AbortController();
 const abort = () => controller.abort();
 let timer;
 const assertScope = () => {
  if (identity !== service.authenticatedIdentity) throw new Error('Account changed. Please retry.');
  if (controller.signal.aborted) throw new Error('Progress image request cancelled.');
 };
 const cancellation = new Promise((_, reject) => {
  controller.signal.addEventListener('abort', () => reject(new Error('Progress image request cancelled.')), { once: true });
 });
 const deadline = new Promise((_, reject) => {
  timer = setTimeout(() => { reject(new Error('Progress image request timed out. Please retry.')); controller.abort(); }, 30000);
 });
 signal?.addEventListener('abort', abort, { once: true });
 if (signal?.aborted) abort();
 try {
  return await Promise.race([cancellation, deadline, (async () => {
   assertScope();
   const auth = await service.getAuthHeaders();
   assertScope();
   if (!auth.Authorization) throw new Error('Please sign in to view or edit progress images.');
   const headers = { Authorization: auth.Authorization };
   if (['POST', 'DELETE'].includes(method) && typeof auth['X-Katafit-Event-Owner'] === 'string') {
    // Keep actor fencing, but never stamp a leader's cached GPS on a peer.
    headers['X-Katafit-Event-Owner'] = auth['X-Katafit-Event-Owner'];
    if (auth['X-Katafit-Event-Owner'] === userId && typeof auth['X-Katafit-Event-Position'] === 'string') {
     headers['X-Katafit-Event-Position'] = auth['X-Katafit-Event-Position'];
    }
   }
   let body;
   if (image) { body = new FormData(); body.append('image', image); }
   const response = await service.fetchApi(target.href, { method, headers, body, signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store' });
   assertScope();
   if (!response.ok) throw new Error(response.status === 403 ? 'Progress image access denied. Refresh permissions and retry.' : 'Could not load or save progress image. Please retry.');
   const result = await (imageUrl ? response.blob() : response.json());
   assertScope();
   return result;
  })()]);
 } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}
